<script lang="ts" context="module">
  // How much of each end of the route to hide, for the privacy of where rides start and end.
  export const TRIM_KM = 0.5
</script>

<script lang="ts">
  // The GPS trace drawn as a glowing line, fitted to the box. With `trim`, the first and last
  // TRIM_KM (at most 10% of the route each) are cut off.
  import type { ReportData } from '../types'

  export let route: NonNullable<ReportData['route']>
  export let trim = true
  export let width = 600
  export let height = 600
  export let color = 'var(--accent)'
  export let strokeWidth = 7
  export let padding = 24
  export let showEnds = true
  export let glow = true

  const uid = Math.random().toString(36).slice(2, 8)

  $: indices = (() => {
    const { dist } = route
    const total = dist[dist.length - 1] - dist[0]
    if (!trim || !(total > 0)) return [0, dist.length - 1]
    const cut = Math.min(TRIM_KM, total * 0.1)
    let from = 0
    let to = dist.length - 1
    while (from < to && dist[from] - dist[0] < cut) from++
    while (to > from && dist[dist.length - 1] - dist[to] < cut) to--
    return [from, to]
  })()

  $: points = (() => {
    const [from, to] = indices
    const lat = route.lat.slice(from, to + 1)
    const lng = route.lng.slice(from, to + 1)
    if (lat.length < 2) return []
    const midLat = (Math.min(...lat) + Math.max(...lat)) / 2
    const kx = Math.cos((midLat * Math.PI) / 180)
    const xs = lng.map((v) => v * kx)
    const ys = lat.map((v) => -v)
    const minX = Math.min(...xs), maxX = Math.max(...xs)
    const minY = Math.min(...ys), maxY = Math.max(...ys)
    const spanX = maxX - minX || 1e-6
    const spanY = maxY - minY || 1e-6
    const scale = Math.min((width - padding * 2) / spanX, (height - padding * 2) / spanY)
    const offX = (width - spanX * scale) / 2
    const offY = (height - spanY * scale) / 2
    return xs.map((px, i) => [offX + (px - minX) * scale, offY + (ys[i] - minY) * scale] as [number, number])
  })()

  $: d = points.map(([px, py], i) => `${i ? 'L' : 'M'}${px.toFixed(1)},${py.toFixed(1)}`).join('')
  $: start = points[0]
  $: end = points[points.length - 1]
</script>

<svg {width} {height} viewBox="0 0 {width} {height}" class="xr-route" aria-hidden="true">
  {#if glow}
    <defs>
      <filter id="glow-{uid}" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation={strokeWidth * 1.4} />
      </filter>
    </defs>
    <path {d} fill="none" style="stroke: {color}" stroke-width={strokeWidth * 2} stroke-opacity="0.55" stroke-linecap="round" stroke-linejoin="round" filter="url(#glow-{uid})" />
  {/if}
  <path {d} fill="none" style="stroke: {color}" stroke-width={strokeWidth} stroke-linecap="round" stroke-linejoin="round" />
  {#if showEnds && start && end}
    <circle cx={start[0]} cy={start[1]} r={strokeWidth * 1.4} fill="#ffffff" style="stroke: {color}" stroke-width={strokeWidth * 0.6} />
    <circle cx={end[0]} cy={end[1]} r={strokeWidth * 1.4} style="fill: {color}" stroke="#ffffff" stroke-width={strokeWidth * 0.6} />
  {/if}
</svg>

<style>
  .xr-route {
    display: block;
    overflow: visible;
  }
</style>
